import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Box, Spinner } from '@chakra-ui/react';
import moment from 'moment';

import { useHeader } from '@app/components/common/navigation/hooks/header-service';
import { api } from '@app/api';
import { useAlert, useAutocomplete } from '@app/hooks';
import { Paper } from '@app/components/common';
import Error404 from '@app/pages/errors/404';
import { usePermissions } from '@app/hooks/use-permissions';
import { ROLES } from '@app/navigation/types';
import { API_DATE_FORMAT, ValidateError, validateInn } from '@app/utils';
import { MasterOutlet, NewOutlet } from '@app/api/models';

import { DDOutletData, MasterOutletFormInstance } from './types';
import MasterOutletForm from './create-form';
import { mapInstance } from './utils/mapInstance';
import { CancelDialog } from './modals/cancel';

const OutletCreate = () => {
  useHeader('Добавить мастер-ТТ', []);

  const history = useHistory();
  const { success, error } = useAlert();
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [instance, setInstance] = useState<MasterOutletFormInstance>();
  const getAutocomplete = useAutocomplete();
  const { state } = useLocation<{ outlet?: NewOutlet }>();
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);

  const onFormSubmit = async (instance: Partial<MasterOutletFormInstance>) => {
    setIsFetching(true);
    try {
      const response = await api.acb.createMasterOutlet(mapInstance(instance));
      if (response.data.code === 200) {
        return response.data.data.id;
      }

      setIsFetching(false);
      error(response.data.message);
    } catch (e) {
      console.log(e);

      setIsFetching(false);
      error(e.message);
    }
  };

  const onCreateFailed = () => {
    setIsFetching(false);
  };

  const onCreateSuccess = (outletId: number, instance: Partial<MasterOutletFormInstance>) => {
    setIsFetching(false);
    success('Мастер ТТ создана');
    api.socket.updateStatusACL(instance.outlet_acb_id.toString(), 'acl-new-outlet', 'completed', {
      masterOutletId: '' + outletId,
      outletName: instance.name,
    });
    history.goBack();
  };

  const onFormCancel = () => {
    history.goBack();
  };

  const canCreate = usePermissions({ roles: [ROLES.ADMIN, ROLES.CHIEF] });

  useEffect(() => {
    const getLegalData = async (inn: string) => {
      const e: ValidateError = { code: 0, message: '' };
      if (!inn || !validateInn(inn, e)) {
        error(e.message);
        return;
      }
  
      const data: DDOutletData[] = await getAutocomplete('dd/legal', {
        search: inn,
      }) as DDOutletData[];
  
      if (data.length) {
        return {
          legal_name: data[0].name,
          legal_address: data[0].address || '',
          kpp: data[0].kpp,
          ogrn: data[0].ogrn,
          okpo: data[0].okpo,
        };
      } else {
        error(e.message || 'Проверьте ИНН, не удалось получить данные организации по ИНН');
        return;
      }
    };

    async function fetchData() {
      if (state?.outlet) {
        setIsFetching(true);
        const { outlet } = state;
        const { data, status } = await api.acb.getNewOutlet(Number(outlet.id));

        if(status !== 200) {
          setIsFetching(false);
          return;
        }

        let parent: MasterOutlet | null = null;
        if (data.data.parent_id) {
          const { data: parentData } = await api.acb.getMasterOutlet(Number(data.data.parent_id));
          parent = parentData.data;
        }

        // const regions = await getAutocomplete('region', { search: outlet.address?.region });
        // const cities = await getAutocomplete('city', { search: outlet.address?.city });
        // const region = regions?.[0];
        // const city = cities?.[0];

        setInstance({
          ...outlet,
          ...data.data,
          id: undefined,
          address: {
            address: outlet.address?.address,
            full_address: outlet.address?.address,
            region: outlet.address?.region ? { id: undefined, name: outlet.address?.region } : undefined,
            city: outlet.address?.city ? { id: undefined, name: outlet.address?.city } : undefined,
            latlng: outlet.address?.latlng,
          },
          geocodedAddress: outlet.address?.id
            ? { ...outlet.address, address: outlet.address?.full_address }
            : null,
          outlet_acb_id: Number(outlet.id),
          status: { id: 1, name: 'Открыта' },
          room_type: { id: 1, name: 'Торговая точка' },
          legal_name: outlet.name,
          name: outlet.name,
          director: outlet.director,
          phone: outlet.phone,
          email: outlet.email,
          price: outlet.price_list,
          open_date: moment().format(API_DATE_FORMAT),
          photos:
            outlet.images?.map(({ image_pid }) => ({
              image_pid,
              filename: '',
              content: '',
            })) || [],
          ...(await getLegalData(outlet.inn)),
          parent,
          segment: parent?.segment,
          sfa_id: 1,
        });

      }
      setIsFetching(false);
    }
    fetchData();
  }, []);


  const onDecline = async (comment: string) => {
    setIsFetching(true);
    try {
      const response = await api.acb.editNewOutlet({ 
        id: Number(state.outlet.id), 
        status_id: 3,
        comment 
      });
      if (response.data.code === 200) {
        setIsFetching(false);
        api.socket.updateStatusACL(state.outlet.id.toString(), 'acl-new-outlet', 'rejected', { 
          outletName: state.outlet.name,
          comment 
        });
        success('Заявка на новую ТТ отклонена');
        history.goBack();
      } else {
        setIsFetching(false);
        error(response.data.message);
      }
    } catch (e) {
      console.log(e);

      setIsFetching(false);
      error(e.message);
    }
  };

  const onDeclineClick = async () => {
    setShowDeclineDialog(true);
  };

  if (!canCreate) {
    return <Error404 />;
  }

  return (<>
    <Box paddingX={{ xs: 0, sm: 2, md: 4, lg: 12, xl: 22 }}>
      {isFetching ? (
        <Box
          width="100%"
          height="300px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Spinner />
        </Box>
      ) : (<Paper>
        <Box paddingX={4} paddingY={6}>
          <MasterOutletForm
            instance={instance}
            onSubmit={onFormSubmit}
            onCreateSuccess={onCreateSuccess}
            onCreateFailed={onCreateFailed}
            submitText='Создать'
            onCancel={onFormCancel}
            submitAndSendText='Отклонить'
            onDeclineForm={onDeclineClick}
          />
        </Box>  
      </Paper>)}
    </Box>
    <CancelDialog open={showDeclineDialog} onAction={onDecline} onCancel={() => setShowDeclineDialog(false)} /> 
  </>);
};

export default OutletCreate;
